import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgFor, NgIf } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, computed, Inject, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { CountBadgeComponent } from '../../../../shared/components/count-badge/count-badge.component';
import { StatusCategoryBadgeComponent } from '../../../../shared/components/status-category-badge/status-category-badge.component';
import {
  IKanbanColumn,
  TBulkCreateKanbanColumn,
  TBulkUpdateKanbanColumn,
} from '../../../../shared/services/kanban-column/kanban-column.interface';
import { KanbanColumnService } from '../../../../shared/services/kanban-column/kanban-column.service';
import { HttpUtilitiesService, IGetManyResponse } from '../../../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../utilities/snackbar.service';
import { IWorkflowStepOption } from '../../workflow-management/workflows/workflows.interface';

export interface IKanbanViewColumnsDialogData {
  kanbanViewId: number;
  kanbanViewName: string;
  // The seeded default view is immutable — the dialog opens in view-only mode (no drag, no save).
  readOnly?: boolean;
}

interface IEditableColumn {
  id: number | null;
  title: string;
  workflowStepIds: number[];
}

// An editable column with its workflow step ids resolved to step objects (for rendering & drag-drop).
interface IColumnView extends IEditableColumn {
  steps: IWorkflowStepOption[];
}

const PALETTE_LIST_ID = 'kc-palette';
const COLUMN_LIST_PREFIX = 'kc-col-';

@Component({
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    CountBadgeComponent,
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatMiniFabButton,
    NgFor,
    NgIf,
    StatusCategoryBadgeComponent,
    TranslateModule,
  ],
  providers: [HttpUtilitiesService],
  selector: 'app-kanban-view-columns-dialog',
  standalone: true,
  styleUrl: './kanban-view-columns-dialog.component.scss',
  templateUrl: './kanban-view-columns-dialog.component.html',
})
export class KanbanViewColumnsDialogComponent implements OnInit {
  public static readonly dialogParameters = {
    disableClose: true,
    maxWidth: '100%',
    restoreFocus: false,
    width: EDialogWidth.large,
  };

  public readonly paletteListId = PALETTE_LIST_ID;
  public readonly columnListPrefix = COLUMN_LIST_PREFIX;

  public readonly columns: WritableSignal<IEditableColumn[]> = signal<IEditableColumn[]>([]);
  public readonly steps: WritableSignal<IWorkflowStepOption[]> = signal<IWorkflowStepOption[]>([]);
  public readonly loading: WritableSignal<boolean> = signal<boolean>(true);
  public readonly saving: WritableSignal<boolean> = signal<boolean>(false);

  // The seeded default view is immutable — the dialog opens in view-only mode (no drag, no save).
  public readonly isReadOnly: boolean;

  // Palette shows only statuses not yet assigned to any column: a status belongs to exactly one column.
  public readonly unassignedSteps: Signal<IWorkflowStepOption[]> = computed(() => {
    const assigned = new Set<number>(this.columns().flatMap((column: IEditableColumn) => column.workflowStepIds));

    return this.steps().filter((step: IWorkflowStepOption) => !assigned.has(step.id));
  });

  // Columns with their workflow step ids resolved to step objects. Recomputed only when columns/steps change,
  // so the template never resolves steps (or allocates arrays) on every change-detection cycle.
  public readonly columnViews: Signal<IColumnView[]> = computed(() => {
    const stepById = new Map<number, IWorkflowStepOption>(
      this.steps().map((step: IWorkflowStepOption) => [step.id, step]),
    );

    return this.columns().map((column: IEditableColumn) => ({
      ...column,
      // Preserve the assignment order of the column.
      steps: column.workflowStepIds
        .map((id: number) => stepById.get(id))
        .filter((step: IWorkflowStepOption | undefined): step is IWorkflowStepOption => step !== undefined),
    }));
  });

  public readonly columnListIds: Signal<string[]> = computed(() => [
    this.paletteListId,
    ...this.columns().map((_column: IEditableColumn, index: number) => `${this.columnListPrefix}${index}`),
  ]);

  private originalColumnIds: number[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: IKanbanViewColumnsDialogData,
    private readonly kanbanColumnService: KanbanColumnService,
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    public readonly dialogRef: MatDialogRef<KanbanViewColumnsDialogComponent, boolean>,
    private readonly snackbar: SnackbarService,
    private readonly translate: TranslateService,
  ) {
    this.isReadOnly = !!this.data.readOnly;
  }

  public ngOnInit(): void {
    forkJoin({
      columns: this.kanbanColumnService.getColumns(this.data.kanbanViewId),
      steps: this.getWorkflowSteps(),
    }).subscribe({
      next: ({ columns, steps }) => {
        this.steps.set(steps.data);
        this.originalColumnIds = columns.data.map((column: IKanbanColumn) => column.id);
        this.columns.set(
          columns.data.map((column: IKanbanColumn) => ({
            id: column.id,
            title: column.title,
            workflowStepIds: column.workflowStepIds ?? [],
          })),
        );
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  public addColumn(): void {
    this.columns.update((columns: IEditableColumn[]) => [...columns, { id: null, title: '', workflowStepIds: [] }]);
  }

  public setColumnTitle(index: number, title: string): void {
    this.columns.update((columns: IEditableColumn[]) =>
      columns.map((column: IEditableColumn, i: number) => (i === index ? { ...column, title } : column)),
    );
  }

  public removeColumn(index: number): void {
    this.columns.update((columns: IEditableColumn[]) => columns.filter((_, i: number) => i !== index));
  }

  public dropColumn(event: CdkDragDrop<IEditableColumn[]>): void {
    this.columns.update((columns: IEditableColumn[]) => {
      const next: IEditableColumn[] = [...columns];
      moveItemInArray(next, event.previousIndex, event.currentIndex);

      return next;
    });
  }

  // targetColumnIndex === -1 means the status was dropped back onto the palette (unassign).
  public onStatusDrop(event: CdkDragDrop<IWorkflowStepOption[]>, targetColumnIndex: number): void {
    const step = event.item.data as IWorkflowStepOption;
    const sourceId: string = event.previousContainer.id;
    const sourceColumnIndex: number = sourceId.startsWith(this.columnListPrefix)
      ? Number(sourceId.slice(this.columnListPrefix.length))
      : -1;

    if (sourceColumnIndex === targetColumnIndex) {
      return;
    }

    this.columns.update((columns: IEditableColumn[]) => {
      const next: IEditableColumn[] = columns.map((column: IEditableColumn) => ({
        ...column,
        workflowStepIds: [...column.workflowStepIds],
      }));

      if (targetColumnIndex >= 0 && !next[targetColumnIndex].workflowStepIds.includes(step.id)) {
        next[targetColumnIndex].workflowStepIds.push(step.id);
      }

      // Dragging out of a column (to the palette or another column) removes it from the source column.
      if (sourceColumnIndex >= 0 && next[sourceColumnIndex]) {
        next[sourceColumnIndex].workflowStepIds = next[sourceColumnIndex].workflowStepIds.filter(
          (id: number) => id !== step.id,
        );
      }

      return next;
    });
  }

  public isValid(): boolean {
    return this.columns().every(
      (column: IEditableColumn) => column.title.trim().length > 0 && column.workflowStepIds.length > 0,
    );
  }

  public save(): void {
    if (this.isReadOnly) {
      this.dialogRef.close(false);

      return;
    }

    if (!this.isValid()) {
      this.snackbar.open(this.translate.instant('page.kanbanConfig.validationError'), ESnackbar.error);

      return;
    }

    const current: IEditableColumn[] = this.columns();
    const { kanbanViewId } = this.data;

    const creates: TBulkCreateKanbanColumn[] = current
      .map((column: IEditableColumn, index: number) => ({ column, index }))
      .filter(({ column }) => column.id === null)
      .map(({ column, index }) => ({
        order: index,
        title: column.title.trim(),
        workflowStepIds: column.workflowStepIds,
      }));

    const updates: TBulkUpdateKanbanColumn[] = current
      .map((column: IEditableColumn, index: number) => ({ column, index }))
      .filter(({ column }) => column.id !== null)
      .map(({ column, index }) => ({
        id: column.id!,
        order: index,
        title: column.title.trim(),
        workflowStepIds: column.workflowStepIds,
      }));

    const keptIds: number[] = updates.map((update: TBulkUpdateKanbanColumn) => update.id);
    const deletedIds: number[] = this.originalColumnIds.filter((id: number) => !keptIds.includes(id));

    const operations: Observable<unknown>[] = [
      ...(creates.length ? [this.kanbanColumnService.bulkCreate(kanbanViewId, creates)] : []),
      ...(updates.length ? [this.kanbanColumnService.bulkUpdate(kanbanViewId, updates)] : []),
      ...(deletedIds.length ? [this.kanbanColumnService.bulkDelete(kanbanViewId, deletedIds)] : []),
    ];

    this.saving.set(true);
    forkJoin(operations.length ? operations : [of(null)]).subscribe({
      next: () => {
        this.snackbar.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
        this.dialogRef.close(true);
      },
      error: () => this.saving.set(false),
    });
  }

  private getWorkflowSteps(): Observable<IGetManyResponse<IWorkflowStepOption>> {
    const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
      fields: ['name', 'statusCategory', 'isDefault'],
      limit: 1000,
      sort: [{ active: 'id', direction: 'asc' }],
    });

    return this.http.get<IGetManyResponse<IWorkflowStepOption>>('workflow-steps', { params });
  }
}
